import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Tag, ArrowRight } from 'lucide-react';

export const Blog = () => {
  const articles = [
    {
      title: 'Solar 101: How Rooftop Solar Works in India',
      excerpt: 'Everything you need to know about solar panels, inverters, net metering, and government policies. A complete beginner\'s guide.',
      category: 'Guide',
      date: 'December 15, 2024',
      slug: 'solar-101-how-rooftop-solar-works',
      image: 'https://images.unsplash.com/photo-1761472823286-9f6093ed6663'
    },
    {
      title: 'On-Grid vs Off-Grid vs Hybrid: Which Solar System Is Right for You?',
      excerpt: 'Understand the key differences between solar system types and choose the best option based on your needs, budget, and backup requirements.',
      category: 'Comparison',
      date: 'December 10, 2024',
      slug: 'on-grid-vs-off-grid-vs-hybrid',
      image: 'https://images.pexels.com/photos/8853525/pexels-photo-8853525.jpeg'
    },
    {
      title: 'Government Solar Subsidies Explained for Homeowners',
      excerpt: 'Learn about PM Surya Ghar scheme and how to claim up to ₹78,000 in subsidies. Complete documentation and eligibility guide.',
      category: 'Finance',
      date: 'December 5, 2024',
      slug: 'government-solar-subsidies',
      image: 'https://images.unsplash.com/photo-1726866492047-7f9516558c6e'
    },
    {
      title: '5 Mistakes to Avoid Before Installing Solar Panels',
      excerpt: 'Common pitfalls that homeowners make when going solar and how to avoid them for maximum savings and system performance.',
      category: 'Tips',
      date: 'November 28, 2024',
      slug: '5-mistakes-to-avoid',
      image: 'https://images.unsplash.com/photo-1715596636360-c47539cbfbbe'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      <section className="py-20 md:py-32" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-7xl font-heading font-semibold text-primary tracking-tight leading-[1.1] mb-6">
              Solar Energy Blog
            </h1>
            <p className="text-lg md:text-xl text-stone-600 leading-relaxed max-w-3xl mx-auto">
              Expert insights, guides, and tips to help you make informed decisions about solar energy.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {articles.map((article, index) => (
              <motion.div
                key={article.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={`/blog/${article.slug}`} className="block group">
                  <div className="bg-white rounded-3xl overflow-hidden border border-stone-100 shadow-card hover:shadow-hover transition-all duration-300">
                    <div 
                      className="h-64 bg-cover bg-center"
                      style={{ backgroundImage: `url(${article.image})` }}
                    />
                    <div className="p-8">
                      <div className="flex items-center space-x-4 mb-4">
                        <span className="inline-block px-3 py-1 bg-accent/10 text-accent text-sm font-medium rounded-full">
                          {article.category}
                        </span>
                        <span className="flex items-center text-sm text-stone-500">
                          <Calendar className="w-4 h-4 mr-1" />
                          {article.date}
                        </span>
                      </div>
                      <h2 className="text-2xl font-heading font-semibold text-primary mb-3 group-hover:text-[#1a5c45] transition-colors">
                        {article.title}
                      </h2>
                      <p className="text-stone-600 mb-4">{article.excerpt}</p>
                      <span className="inline-flex items-center text-primary font-medium group-hover:translate-x-2 transition-transform">
                        Read More <ArrowRight className="w-4 h-4 ml-2" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
